import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const Config = () => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings/config');
      setConfig(response.data || {});
    } catch (error) {
      toast.error('Failed to fetch configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (field) => {
    const newValue = !config[field];
    setConfig(prev => ({ ...prev, [field]: newValue }));
    
    try {
      await axios.put('/api/settings/config', { [field]: newValue });
      toast.success('Setting updated successfully');
    } catch (error) {
      toast.error('Failed to update setting');
      setConfig(prev => ({ ...prev, [field]: !newValue })); // Revert
    }
  };

  const handleTextChange = async (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    
    // Debounce the API call
    clearTimeout(window.configUpdateTimeout);
    window.configUpdateTimeout = setTimeout(async () => {
      try {
        await axios.put('/api/settings/config', { [field]: value });
        
        // Sync taxRate to Organization vatPercentage
        if (field === 'taxRate') {
          const vatValue = parseFloat(value);
          await axios.put('/api/organization', { vatPercentage: isNaN(vatValue) ? 0 : vatValue });
        }
      } catch (error) {
        toast.error('Failed to update setting');
      }
    }, 500);
  };

  const Toggle = ({ field, label }) => (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-700">{label}</span>
      <button
        onClick={() => handleToggle(field)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          config[field] ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            config[field] ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-4xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Configuration</h2>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Configuration</h2>

      <div className="space-y-6">
        {/* Invoice/Quotation Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Invoice / Quotation Settings</h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quotation Heading</label>
                <input
                  type="text"
                  value={config.quotationHeading || ''}
                  onChange={(e) => handleTextChange('quotationHeading', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Heading</label>
                <input
                  type="text"
                  value={config.invoiceHeading || ''}
                  onChange={(e) => handleTextChange('invoiceHeading', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">VAT Percentage (%)</label>
                <input
                  type="number"
                  value={config.taxRate !== undefined ? config.taxRate : 5}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    handleTextChange('taxRate', isNaN(val) ? 0 : val);
                  }}
                  step="0.1"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Log Management</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Auto-Delete Logs Older Than (Days)
                </label>
                <input
                  type="number"
                  value={config.logRetentionDays !== undefined ? config.logRetentionDays : 30}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 30 : parseInt(e.target.value);
                    handleTextChange('logRetentionDays', isNaN(val) ? 30 : val);
                  }}
                  min="1"
                  max="365"
                  className="w-48 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Logs older than this will be automatically deleted daily at 2 AM
                </p>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Display Options</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Toggle field="showLogo" label="Show Logo" />
                  <Toggle field="showBusinessContact" label="Business Contact" />
                  <Toggle field="showLocation" label="Location" />
                  <Toggle field="showInvoicePrefix" label="Invoice Prefix" />
                  <Toggle field="showSalesWorker" label="Sales Worker" />
                  <Toggle field="showAmountInWords" label="Amount in Words" />
                </div>
                <div className="space-y-2">
                  <Toggle field="showPaymentTerms" label="Payment Terms" />
                  <Toggle field="showTermsConditions" label="Terms & Conditions" />
                  <Toggle field="showVehicleInfo" label="Vehicle Information" />
                  <Toggle field="showSignature" label="Signature Block" />
                  <Toggle field="showVatComments" label="VAT/Comments per Part" />
                  <Toggle field="showCustomerInfo" label="Customer Info Repeat" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Job Card Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Job Card Settings</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-3">Visibility Options</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Toggle field="jobCardShowCustomer" label="Show Customer/Contact" />
                  <Toggle field="jobCardShowVehicle" label="Show Vehicle Selector" />
                </div>
                <div className="space-y-2">
                  <Toggle field="jobCardShowOperator" label="Show Sales Operator" />
                  <Toggle field="jobCardShowDateTime" label="Show Appointment Date/Time" />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Requirements</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Toggle field="jobCardRequireVehicle" label="Require Vehicle" />
                  <Toggle field="jobCardRequireWorker" label="Require Sales Worker" />
                  <Toggle field="jobCardRequireVideo" label="Require Before/After Video" />
                </div>
                <div className="space-y-2">
                  <Toggle field="jobCardRequirePhotos" label="Require Chassis/Mileage Photos" />
                  <Toggle field="jobCardEnableWarranty" label="Enable Warranty" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Config;
